// Every calendar function arrives through `el.call`, never an import (FA001):
// a relative sibling import is inlined into this component file by the
// bundler, so frank publishes a body whose identifier resolves to nothing, and
// the deep `@symbo-ls/workspace-calendar/functions/calendarUtils.js`
// bare-package subpath self-imports this package's own barrel and throws a
// circular-import TDZ crash (confirmed live via `bun test`) — see
// QuickCreate.js's note. CalEventDetailModal is EAGER and its shell-root
// opener fires on routes that never fold the calendar context, so every key it
// calls is registered by functions/eagerSlice.js as well as functions/index.js.
// dateUtils.js stays the ORIGINAL deep `@symbo-ls/workspace-shared` subpath on purpose:
// switching it to the bare package root pulls in shared's ENTIRE
// components+functions+state barrel, which live-reproduced a SEPARATE
// circular-import TDZ crash against `workspace/functions/featureFlags.js`
// (confirmed via `bun test`, not hypothetical — see the note left on
// tickets/smbls.md). No confirmed prod evidence this deep subpath is
// actually broken (unlike calendarUtils.js, which had 8 live pageerrors) —
// not swapping an unverified maybe-bug for a confirmed one.
// Role helpers come straight from the workspace-shell library, not through
// calendarUtils.js: that module no longer re-exports them (frank FA206
// stub-phantom — a bag re-export of an externalized value publishes as "").
import { currentUser } from '@symbo-ls/workspace-shell/functions/roleHelpers.js'
import { localTime, localDate } from '@symbo-ls/workspace-shared/functions/dateUtils.js'

// Resolve the viewer's timezone — prefer the user's profile timezone over
// the browser's system clock. Fixes WORKSPACE-CAL-TZ-DISPLAY where a user
// in Asia/Tbilisi (UTC+4) saw event times in UTC because their browser
// timezone didn't match their profile.
const _userTz = (root) => {
  const me = currentUser(root)
  return me?.timezone || root?.user?.timezone || null
}

// Format an event's time range using the user's profile timezone when
// available. Falls back to the event's pre-computed startTime/endTime
// (which were converted using whatever TZ was active at fetch time —
// usually the browser's).
const _formatRange = (ev, tz) => {
  if (tz && ev.startAt && ev.endAt) {
    return `${localTime(new Date(ev.startAt), tz)} – ${localTime(new Date(ev.endAt), tz)}`
  }
  return `${ev.startTime || ''} – ${ev.endTime || ''}`
}

const _formatDate = (ev, tz) => {
  if (tz && ev.startAt) return localDate(new Date(ev.startAt), tz)
  return ev.date || ''
}

const _eventParticipants = (el, root) => {
  const ev = root?.selectedEvent
  if (!ev) return []
  const emails = new Set(ev.invitees || [])
  const memberKeys = new Set(ev.integrationMetadata?.calendarInviteeMemberKeys || [])
  const people = root.people || []
  const matchedEmails = new Set()
  const participants = people.filter((person) => {
    const selected =
      emails.has(person.email) || memberKeys.has(el.call('calendarTeamMemberKey', person))
    if (selected && person.email) matchedEmails.add(person.email)
    return selected
  })
  for (const email of emails) {
    if (!matchedEmails.has(email)) participants.push({ email, name: email })
  }
  return participants
}

const _viewerIsParticipant = (el, root) => {
  const participants = _eventParticipants(el, root)
  const viewer = currentUser(root) || { email: root?.currentUserEmail }
  const viewerEmail = String(viewer?.email || root?.currentUserEmail || '').toLowerCase()
  const viewerKey = el.call('calendarTeamMemberKey', viewer)
  return participants.some((participant) => {
    const participantEmail = String(participant?.email || '').toLowerCase()
    if (viewerEmail && participantEmail === viewerEmail) return true
    return !!viewerKey && el.call('calendarTeamMemberKey', participant) === viewerKey
  })
}

const _setRsvpWithScope = (el, root, event, status) => {
  const recurrence = el.call('inferRecurrence', root?.events, event)
  if (recurrence.recurring === 'none') {
    el.call('setEventRsvp', event, status, 'this')
    return
  }
  const title = el.call('polyglot', 'calendar.rsvp.scopeHeading')
  el.call('openModal', {
    template: 'custom',
    maxHeight: '90vh',
    title,
    content: {
      flow: 'y',
      gap: 'B',
      minWidth: '0',
      overflowX: 'hidden',
      Hgroup: {
        minWidth: '0',
        H: { tag: 'h5', fontSize: 'B', text: title, overflowWrap: 'anywhere' },
        P: {
          text: el.call('polyglot', 'calendar.rsvp.scopeSubtitle'),
          color: 'inactive',
          overflowWrap: 'anywhere'
        }
      },
      ModalFooter: {
        flow: 'y',
        gap: 'Z1',
        CancelBtn: {
          extends: 'Button',
          theme: 'tertiary',
          width: '100%',
          order: '3',
          text: el.call('polyglot', 'calendar.rsvp.scopeCancel'),
          onClick: (e, button) => button.call('closeModal')
        },
        ThisBtn: {
          extends: 'Button',
          theme: 'tertiary',
          width: '100%',
          order: '1',
          text: el.call('polyglot', 'calendar.rsvp.scopeThis'),
          onClick: (e, button) => {
            button.call('setEventRsvp', event, status, 'this')
            button.call('closeModal')
          }
        },
        FutureBtn: {
          extends: 'Button',
          theme: 'primary',
          width: '100%',
          order: '2',
          text: el.call('polyglot', 'calendar.rsvp.scopeFuture'),
          onClick: (e, button) => {
            button.call('setEventRsvp', event, status, 'future')
            button.call('closeModal')
          }
        }
      }
    }
  })
}

// For recurring events, inherit missing fields from a sibling instance
const _inheritFromSeries = (ev, events) => {
  if (!ev || (ev.invitees?.length && ev.description !== undefined)) return ev
  const baseId = ev.googleEventId?.replace(/_\d{8}T\d{6}Z$/, '')
  if (!baseId) return ev
  const sibling = (events || []).find(
    (e) =>
      e.id !== ev.id &&
      e.googleEventId?.replace(/_\d{8}T\d{6}Z$/, '') === baseId &&
      (e.invitees?.length || e.description)
  )
  if (!sibling) return ev
  return {
    ...ev,
    invitees: ev.invitees?.length ? ev.invitees : sibling.invitees,
    description: ev.description ?? sibling.description,
    type: ev.type || sibling.type
  }
}

const _clearEventHash = (eventId) => {
  if (typeof window === 'undefined') return
  // Primary: clear the `?event=` deep-link query param (tickets-style) on
  // any close path — including inline `selectedEvent: null` updates that
  // don't route through closeEventDetailModal's onClose. Only while it names
  // the event that closed: a close that a navigation causes runs on the NEW
  // address, which can already name ANOTHER event (Back / Forward to its
  // link), and that link must stay for its own restore.
  try {
    const url = new URL(window.location.href)
    const named = url.searchParams.get('event')
    if (named !== null && (eventId == null || named === String(eventId))) {
      for (const key of [
        'event',
        'eventSeries',
        'eventFingerprint',
        'eventSnapshot',
        'eventRsvp',
        'eventRsvpScope'
      ]) {
        url.searchParams.delete(key)
      }
      window.history.replaceState(null, '', url.toString())
    }
  } catch (_) {}
  // Legacy: older links may still carry `#event=` in the hash.
  const hash = window.location.hash || ''
  if (hash.includes('event=')) {
    const next = '#view=' + (hash.match(/view=([^&]*)/)?.[1] || 'week')
    window.history.replaceState(null, '', next)
  }
}

// Body-only component — backdrop, ESC binding, dismiss-on-backdrop are
// owned by @symbo.ls/windows ModalLayer (renders inside CustomTemplate
// Slot). Triggered via `el.call('openEventDetailModal', event)`.
//
// Close paths:
// - External close (button, etc.) → `el.call('closeEventDetailModal')`.
// - Inline close from a body action (delete event, edit-and-open-new-
//   booking, etc.) — those handlers continue to set
//   `selectedEvent: null` as part of their own state update. The
//   `subscribeTo` + onUpdate watcher below detects the transition and
//   pops the layer automatically, so handlers don't have to know about
//   the modal layer lifecycle.
//
// The lazy invitee-load side effect that used to live in the reactive
// `display:` factory now fires from an onRender hook — when the body
// mounts (layer push), check whether the event needs enrichment and
// fire fetchEventDetails.
export const CalEventDetailModal = {
  maxWidth: 'I', // ~800px
  widthRange: '0 100%',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  padding: 'Z',

  // Explicit viewport close — `position: fixed` so it always lands at the
  // viewport top-right regardless of where the modal body's container lives
  // in the layout. Geometry is the brand CloseButton (F2: one X, one size);
  // only the placement + the floating `overlay` fill/shadow (it sits over the
  // backdrop, not on a panel surface) are local.
  CloseBtn: {
    extends: 'CloseButton',
    position: 'fixed',
    top: 'A',
    right: 'A',
    zIndex: '2100',
    background: 'overlay',
    // Token color, not a raw rgba() literal — same `--color-shadow` idiom as
    // QuickCreate/NewBookingModal's floating panels in this package.
    boxShadow: '0 2px 12px var(--color-shadow)',
    onClick: (e, el) => {
      e.preventDefault()
      e.stopPropagation()
      el.call('closeEventDetailModal')
    }
  },

  subscribeTo: ['selectedEvent'],
  onRender: (el, s) => {
    const ev = s.root?.selectedEvent
    if (!ev) return
    if (el._lastDetailId === ev.id) return
    // Try inheriting from a sibling immediately for instant display.
    // The previous `if (ev.invitees !== undefined) return` guard was
    // dropped 2026-06-03 because callers now PRE-NORMALIZE invitees
    // to `[]` (and description to `null`) in openEventDetailModal so
    // the skeleton state can't get stuck on summary feeds. The
    // dedup is owned by fetchEventDetails' internal `_detailCache`
    // so re-firing here is cheap.
    const enriched = _inheritFromSeries(ev, s.root?.events)
    if (enriched !== ev && enriched.invitees?.length) {
      s.root.update({ selectedEvent: enriched })
    }
    el._lastDetailId = ev.id
    el.call('fetchEventDetails', ev.id, s.root)
  },
  onUpdate: (el, s) => {
    // Hash sync — clear the URL hash when selectedEvent goes null. We
    // used to ALSO fire `el.call('closeModal', 'cal-event-detail')`
    // here, but that double-closed when the user already triggered
    // close via the X button (closeEventDetailModal pops the layer
    // AND clears selectedEvent → onUpdate then tried to pop again,
    // leaving the windows-plugin stack in a broken state where the
    // backdrop stayed visible). Single source of truth for layer
    // pops: closeEventDetailModal for explicit closes, the
    // ModalLayer's backdrop-dismiss handler for clicks outside.
    if (s.root?.selectedEvent) {
      el._wasOpen = true
      el._openEventId = s.root.selectedEvent.id
    } else if (el._wasOpen) {
      el._wasOpen = false
      _clearEventHash(el._openEventId)
    }
  },

  ModalHeader: {
    position: 'relative',
    Hgroup: {
      paddingRight: 'E',
      H: {
        text: (el, s) => {
          const ev = s.root?.selectedEvent
          if (!ev) return ''
          if (el.call('eventIsPrivateFor', ev, currentUser(s.root)))
            return el.call('polyglot', 'calendar.detail.privateTitle')
          return ev.title || ''
        }
      },
      P: {
        text: null,
        flow: 'x',
        align: 'center left',
        gap: 'Z1',
        Icon: { name: 'clock' },
        Text: {
          tag: 'span',
          text: (el, s) => {
            const ev = s.root?.selectedEvent
            if (!ev) return ''
            const tz = _userTz(s.root)
            return `${_formatDate(ev, tz)} · ${_formatRange(ev, tz)}`
          }
        }
      }
    },
    Icons: {
      position: 'absolute',
      top: '0',
      right: '0',
      margin: '0',
      gap: 'Y',
      align: 'center right',
      // Override the brand's inherited Close — dispatch the windows-
      // layer close helper which clears selectedEvent + showEventDetail
      // and pops the layer. Replaces the older `closeModalByStateKey`
      // path that depended on a stateKey declaration this component
      // no longer has.
      //
      // Named like Edit / Delete beside it. The override replaces the brand
      // header's own X (a CloseButton that carries "Close"), and an
      // IconButton with no 'aria-label' and no title renders no name at all:
      // a screen reader announced a bare "button".
      Close: {
        extends: 'IconButton',
        'aria-label': (el) => el.call('polyglot', 'common.action.close'),
        title: (el) => el.call('polyglot', 'common.action.close'),
        Icon: { name: 'close' },
        onClick: (e, el) => {
          e.preventDefault()
          e.stopPropagation()
          el.call('closeEventDetailModal')
        }
      },
      // Edit and Delete show only to whoever may write the event — its
      // organizer or a platform superadmin (calendarCanWriteEvent mirrors the
      // server rule). An invitee sees neither.
      EditBtn: {
        extends: 'IconButton',
        hide: (el, s) => !el.call('calendarCanWriteEvent', s.root?.selectedEvent, s.root),
        padding: 'Y2',
        round: 'radiusPill',
        'aria-label': (el) => el.call('polyglot', 'calendar.detail.edit'),
        title: (el) => el.call('polyglot', 'calendar.detail.edit'),
        Icon: { name: 'edit' },
        onClick: (e, el, s) => {
          const raw = s.root?.selectedEvent
          if (!raw) return
          const ev = _inheritFromSeries(raw, s.root?.events)
          // A generated occurrence carries its own date (getEventsForDay
          // spreads `date: <clicked day>` so DELETION can scope to the
          // occurrence), but the EDIT form must seed from the SERIES SOURCE
          // row: Update saves by id onto that row, and seeding the
          // occurrence date would silently move the whole series anchor on
          // an unrelated edit. Google-synced rows are their own source
          // (found by id), so their per-occurrence date is unchanged here.
          const sourceRow = (s.root?.events || []).find((item) => item.id === raw.id)
          // Infer recurrence from sibling instances for Google-synced events
          const inferred = el.call('inferRecurrence', s.root?.events, ev)
          // Detect meeting mode from existing event data
          const desc = ev.description || ''
          let meetingMode = ev.meetingMode || 'symbols-meet'
          let meetUrl = ev.meetUrl || ''
          if (!ev.meetingMode || ev.meetingMode === 'online') {
            if (desc.includes('/meet#room=')) meetingMode = 'symbols-meet'
            else if (ev.meetUrl || desc.match(/https?:\/\//)) {
              meetingMode = 'external-url'
              if (!meetUrl && desc.match(/https?:\/\/\S+/))
                meetUrl = desc.match(/https?:\/\/\S+/)[0]
            } else meetingMode = 'symbols-meet'
          }
          // Close this layer first, then open the new-booking layer with
          // the edit draft — this fires on ANY route now (CalNewBookingModal
          // is a global windows-layer modal, not a /calendar-only mount), so
          // `el.call('openNewBookingModal', overrides)` replaces the old
          // `root.update({ showNewBooking: true, newBooking: freshBooking(...) })`
          // pair. Overrides are passed straight through to freshBooking.
          const booking = {
            title: ev.title,
            date: sourceRow?.date ?? ev.date,
            startTime: ev.startTime,
            endTime: ev.endTime,
            startAt: ev.startAt,
            endAt: ev.endAt,
            invitees: (ev.invitees || []).join(', '),
            inviteeMemberKeys: ev.integrationMetadata?.calendarInviteeMemberKeys || [],
            description: ev.description || '',
            type: ev.type || 'meeting',
            meetingMode,
            meetUrl,
            recurring: inferred.recurring,
            recurringDays: inferred.recurringDays,
            recurrenceEndType: ev.recurrenceEndType || 'after',
            recurrenceEndCount: ev.recurrenceEndCount || 10,
            recurrenceEndDate: ev.recurrenceEndDate || '',
            integrationMetadata: ev.integrationMetadata,
            // Carry the existing reminder forward so it's not silently
            // dropped when editing. (#2200)
            reminderMinutes: ev.reminderMinutes ?? null,
            editingEventId: ev.id
          }
          el.call('closeEventDetailModal')
          // The windows host reuses preallocated modal slots. Reopening a
          // custom modal in the same tick can leave that slot between its
          // hidden and flex states, which makes the edit form align at the
          // viewport edge and can retain an incomplete header subtree. Let
          // the detail layer finish closing before mounting the edit layer.
          requestAnimationFrame(() => el.call('openNewBookingModal', booking))
        }
      },
      DeleteBtn: {
        extends: 'IconButton',
        hide: (el, s) => !el.call('calendarCanWriteEvent', s.root?.selectedEvent, s.root),
        padding: 'Y2',
        round: 'radiusPill',
        'aria-label': (el) => el.call('polyglot', 'calendar.detail.delete'),
        title: (el) => el.call('polyglot', 'calendar.detail.delete'),
        Icon: { name: 'trash' },
        onClick: (e, el, s) => {
          const ev = s.root?.selectedEvent
          if (!ev) return
          el.call('confirmEventDeletion', el, s.root, ev, 'closeEventDetailModal')
        }
      }
    }
  },

  Body: {
    flow: 'y',
    gap: 'A',
    padding: 'Z 0',

    // Linked resources — chat channel(s) / meet room(s) that reference
    // this event. Hidden when there are no links.
    Links: {
      extends: 'LinkedResources',
      type: 'calendar_event',
      id: (el, s) => s?.root?.selectedEvent?.id || null
    },

    // Edit/Delete actions are in the ModalFooter at the bottom of the Window.

    // Proposal banner — visible only when the open event is a pending
    // proposal. Accept moves the original event to the proposed time and
    // removes the proposal duplicate; Decline just removes the proposal.
    // Both ask first (functions/eventProposal.js). Accept shows to whoever
    // may write the original event; Decline also to the person who proposed.
    ProposalBanner: {
      display: (el, s) => (s.root?.selectedEvent?.isProposal ? 'flex' : 'none'),
      flow: 'x',
      align: 'center space-between',
      gap: 'Z',
      padding: 'Z A',
      round: 'radiusControl',
      background: 'overlay',
      flexWrap: 'wrap',

      Text: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'title',
        flex: '1',
        text: (el, s) => {
          const ev = s.root?.selectedEvent
          if (!ev) return ''
          return el
            .call('polyglot', 'calendar.detail.proposedBy')
            .replace(
              '{name}',
              ev.proposedBy || el.call('polyglot', 'calendar.detail.proposedByFallback')
            )
        }
      },
      Actions: {
        flow: 'x',
        gap: 'Y',
        DeclineBtn: {
          extends: 'Button',
          theme: 'secondary',
          hide: (el, s) => !el.call('calendarCanDeclineProposal', s.root?.selectedEvent, s.root),
          text: '{{ calendar.detail.decline | polyglot }}',
          onClick: (e, el, s) => {
            const ev = s.root?.selectedEvent
            if (ev) el.call('declineEventProposal', ev)
          }
        },
        AcceptBtn: {
          extends: 'Button',
          theme: 'primary',
          hide: (el, s) => !el.call('calendarCanAcceptProposal', s.root?.selectedEvent, s.root),
          text: '{{ calendar.detail.accept | polyglot }}',
          onClick: (e, el, s) => {
            const ev = s.root?.selectedEvent
            if (ev) el.call('acceptEventProposal', ev)
          }
        }
      }
    },

    // Creator / organizer row — resolves the creatorEmail to the person's
    // display name (and avatar) via root.people, falls back to the raw
    // email if no profile is loaded.
    CreatorRow: {
      display: (el, s) => {
        const ev = s.root?.selectedEvent
        if (!ev?.creatorEmail) return 'none'
        if (el.call('eventIsPrivateFor', ev, currentUser(s.root))) return 'none'
        return 'flex'
      },
      flow: 'x',
      align: 'center left',
      gap: 'Z1',

      Avatar: {
        avatarType: 'initials',
        boxSize: 'B B',
        src: (el, s) => {
          const ev = s.root?.selectedEvent
          if (!ev) return el.call('avatarUrl', null)
          const creator = el.call('findPerson', ev.creatorEmail)
          return el.call('avatarUrl', (creator && creator.name) || ev.creatorEmail)
        }
      },
      Text: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'subtitle',
        text: (el, s) => {
          const ev = s.root?.selectedEvent
          if (!ev) return ''
          const creator = el.call('findPerson', ev.creatorEmail)
          const name = (creator && creator.name) || ev.creatorEmail
          // A function replacement: a name is the person's own text.
          return el.call('polyglot', 'calendar.detail.createdBy').replace('{name}', () => name)
        }
      }
    },

    // Description and Invitees are lazy-loaded by fetchEventDetails when
    // the modal opens. While loading, both show a skeleton; once loaded,
    // empty/undefined fields hide normally.
    Description: {
      display: (el, s) => {
        const ev = s.root?.selectedEvent
        if (!ev) return 'none'
        if (el.call('eventIsPrivateFor', ev, currentUser(s.root))) return 'none'
        // Loading: description is undefined (not yet fetched)
        if (ev.description === undefined) return 'flex'
        // Loaded: only show if there's content
        return ev.description ? 'flex' : 'none'
      },
      flow: 'x',
      align: 'flex-start left',
      gap: 'Z1',

      Icon: { name: 'menu', opacity: '.5' },

      Text: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z2',
        color: 'subtitle',
        lineHeight: '1.6',
        // A Google Calendar description is HTML, and this printed its tags
        // (`<p>Global Tech Weekend…`). Read it as TEXT with its paragraphs
        // kept — `pre-line` paints the newlines the helper leaves. Never
        // `html:`: an invitation is somebody else's string.
        whiteSpace: 'pre-line',
        text: (el, s) =>
          el.call('htmlToPlainText', s.root?.selectedEvent?.description, { breaks: true }) || ''
      },

      Skeleton: {
        hide: (el, s) => s.root?.selectedEvent?.description !== undefined,
        flex: '1',
        height: 'A',
        round: 'V',
        background: 'overlay',
        opacity: '.6'
      }
    },

    InviteesSection: {
      display: (el, s) => {
        const ev = s.root?.selectedEvent
        if (!ev) return 'none'
        if (el.call('eventIsPrivateFor', ev, currentUser(s.root))) return 'none'
        // Loading: invitees is undefined
        if (ev.invitees === undefined) return 'flex'
        // Loaded: include privacy-projected Team participants identified by
        // stable member id, even when their email is intentionally absent.
        return _eventParticipants(el, s.root).length ? 'flex' : 'none'
      },
      flow: 'y',
      gap: 'Z1',

      HeaderRow: {
        flow: 'x',
        align: 'center left',
        gap: 'Z1',

        Icon: { name: 'user' },
        // Invitees fetch in flight — Skeleton line instead of a "Loading…"
        // string (DESIGN_LANGUAGE.md §9: "Loading…" strings are illegal;
        // Skeleton mirrors the geometry of the text it replaces).
        LabelSkeleton: {
          hide: (el, s) => s.root?.selectedEvent?.invitees !== undefined,
          extends: 'SkeletonRow',
          width: 'F'
        },
        Label: {
          tag: 'span',
          hide: (el, s) => s.root?.selectedEvent?.invitees === undefined,
          fontSize: 'Z2',
          fontWeight: '700',
          color: 'title',
          text: (el, s) => {
            const count = _eventParticipants(el, s.root).length
            return `${count} guest${count !== 1 ? 's' : ''}`
          }
        }
      },

      List: {
        hide: (el, s) => s.root?.selectedEvent?.invitees === undefined,
        flow: 'x',
        align: 'center left',
        flexWrap: 'wrap',
        gap: 'Z',

        childExtends: 'CalInviteeRow',
        childrenAs: 'state',
        children: (el, s) => _eventParticipants(el, s.root)
      },

      SkeletonList: {
        hide: (el, s) => s.root?.selectedEvent?.invitees !== undefined,
        flow: 'y',
        gap: 'Z',
        paddingLeft: 'C',

        Row1: {
          height: 'B',
          round: 'V',
          background: 'overlay',
          opacity: '.6'
        },
        Row2: {
          height: 'B',
          round: 'V',
          background: 'overlay',
          opacity: '.4'
        },
        Row3: {
          height: 'B',
          round: 'V',
          background: 'overlay',
          opacity: '.3'
        }
      }
    },

    RsvpRow: {
      hide: (el, s) => {
        const ev = s.root?.selectedEvent
        return (
          !ev ||
          el.call('eventIsPrivateFor', ev, currentUser(s.root)) ||
          !_viewerIsParticipant(el, s.root)
        )
      },
      flow: 'x',
      align: 'center space-between',
      gap: 'Z',
      flexWrap: 'wrap',
      paddingTop: 'Z',

      Label: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'title',
        text: '{{ calendar.detail.going | polyglot }}'
      },
      Actions: {
        flow: 'x',
        gap: 'Y',

        CalRsvpButton: {
          rsvpStatus: 'accepted',
          text: '{{ calendar.detail.rsvpYes | polyglot }}'
        },
        CalRsvpButton_1: {
          rsvpStatus: 'declined',
          text: '{{ calendar.detail.rsvpNo | polyglot }}'
        },
        CalRsvpButton_2: {
          rsvpStatus: 'tentative',
          text: '{{ calendar.detail.rsvpMaybe | polyglot }}'
        }
      }
    }
  },

  ModalFooter: {
    flow: 'x',
    gap: 'Z1',
    paddingTop: 'A',
    // Cross-app: launch a meet call linked to this calendar event. The
    // event id (bigint) flows into meet_rooms.calendar_event_id, which the
    // meet RLS policies treat as a normal owned room.
    // "Join call" — launches (or rejoins) a meet room linked to this
    // calendar event. Was previously labelled "Save event" which misled
    // users into thinking it would persist edits; edits flow through the
    // EditBtn → CalNewBookingModal pipeline instead. The detail modal is
    // read-only by design. (#2198)
    SaveBtn: {
      extends: 'Button',
      theme: 'primary',
      text: '{{ calendar.detail.joinCall | polyglot }}',
      // Re-entrancy guard against double-clicks during createRoom. Scoped
      // state (vs plain element prop) so the `disabled` binding actually
      // re-evaluates when the flag flips. (#2198)
      state: { joining: false },
      disabled: (el, s) => s.joining,
      onClick: async (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        if (s.joining) return
        const ev = s.root?.selectedEvent
        if (!ev) return
        s.update({ joining: true })
        // Reuse an existing meet_room for this event if present, otherwise
        // create a new one.
        const existing = (s.root.rooms || []).find((r) => r.calendarEventId === ev.id)
        let id = existing?.id
        if (!id) {
          id = await el.call('createRoom', {
            name: ev.title || el.call('polyglot', 'calendar.detail.callName'),
            kind: 'scheduled',
            calendarEventId: ev.id,
            transcribeEnabled: true
          })
        }
        s.update({ joining: false })
        if (!id) {
          el.call('openNotification', {
            type: 'error',
            title: el.call('polyglot', 'calendar.detail.callFailedTitle'),
            message: el.call('polyglot', 'calendar.detail.callFailedMessage')
          })
          return
        }
        s.root.update({
          showEventDetail: false,
          selectedEvent: null,
          activeRoomId: id,
          meetView: 'call'
        })
        if (typeof window !== 'undefined') {
          window.location.hash = `room=${id}&view=call`
        }
        // Walk up to the page root with the `routes` prop — el.getRoot()
        // doesn't exist in DOMQL v3.14.
        let __root = el
        while (__root && !__root.routes) __root = __root.parent
        if (__root) el.router('/meet', __root)
        await el.call('connectCall', id)
      }
    },

    Spacer: { flex: '1' },

    AskAIBtn: {
      extends: 'Button',
      theme: 'secondary',
      flow: 'x',
      gap: 'Z',
      align: 'center center',
      Icon: { name: 'magicstar', boxSize: 'Z2' },
      text: '{{ calendar.detail.askAi | polyglot }}',
      onClick: (e, el, s) => {
        const ev = s.root?.selectedEvent
        if (!ev) return
        const root = s.root
        const id = ev.id
        const when = `${ev.date || ''} ${ev.startTime || ''}${
          ev.endTime ? ` – ${ev.endTime}` : ''
        }`.trim()
        const evFallback = el.call('polyglot', 'calendar.detail.eventFallback')
        const titleLine = `${ev.title || evFallback}${when ? ` · ${when}` : ''}`
        // Modern picked-context path — assistantAttachment was DEAD (nothing in
        // the Simone pipeline read it, so the event never reached the composer).
        // Attach the event as a context chip the transport prepends to the next
        // turn (root.pickedElements, per TicketDetailModal.AskAIBtn); open the
        // assistant and let the user type their question.
        const entry = {
          // keyPath is DISPLAY only — the shared AiContextChip joins it with
          // ' › ' for the chip title and matches entries by `pickedAt`.
          keyPath: [evFallback, titleLine],
          key: ev.title || evFallback,
          tag: 'event',
          title: titleLine,
          pickedAt: Date.now(),
          event: {
            id,
            title: ev.title || '',
            date: ev.date || null,
            startTime: ev.startTime || null,
            endTime: ev.endTime || null
          }
        }
        const prev = (root.pickedElements || []).filter(
          (p) => !(p && p.event && String(p.event.id) === String(id))
        )
        root.update({ assistantOpen: true, pickedElements: [...prev, entry] })
      }
    }
  }
}

export const CalInviteeRow = {
  flow: 'x',
  align: 'center left',
  flex: 'none',
  gap: 'Z1',
  padding: 'Y2 Z',
  round: 'radiusPill',
  background: 'overlay',
  borderWidth: 'hairline',
  borderStyle: 'solid',
  borderColor: (el, s) => {
    const status = el.call('calendarEventRsvpStatus', s.root?.selectedEvent, s)
    if (status === 'accepted') return 'green'
    if (status === 'declined') return 'red'
    if (status === 'tentative') return 'yellow'
    return 'line'
  },

  Avatar: {
    avatarType: 'initials',
    boxSize: 'B1 B1',
    src: (el, s) => el.call('avatarUrl', s.name || s.email)
  },

  Email: {
    tag: 'span',
    fontSize: 'Z2',
    color: 'subtitle',
    text: (el, s) => s.name || s.email || ''
  }
}

export const CalRsvpButton = {
  extends: 'Button',
  theme: 'secondary',
  minWidth: 'C',
  round: 'radiusPill',
  borderWidth: 'hairline',
  borderStyle: 'solid',
  borderColor: (el, s) => {
    const viewer =
      (s.root?.people || []).find((person) => person.email === s.root?.currentUserEmail) ||
      s.root?.currentUserEmail
    const selected = el.call('calendarEventRsvpStatus', s.root?.selectedEvent, viewer)
    if (selected !== el.rsvpStatus) return 'line'
    if (selected === 'accepted') return 'green'
    if (selected === 'declined') return 'red'
    return 'yellow'
  },
  onClick: (event, el, s) => {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
    const selectedEvent = s.root?.selectedEvent
    if (selectedEvent) _setRsvpWithScope(el, s.root, selectedEvent, el.rsvpStatus)
  }
}
